import React from 'react';
import { Box, Typography, Avatar, alpha, useTheme, Grid, Divider, LinearProgress } from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { StyledCard, StyledCardHeader, StyledCardContent, MetricChip, formatDisplayItem } from '@features/analysis/components/StyledComponents';
import { AIAnalysisResult } from '@features/analysis/types';

interface PerformanceMetricsCardProps {
  analysis: AIAnalysisResult;
}

const PerformanceMetricsCard: React.FC<PerformanceMetricsCardProps> = ({ analysis }) => {
  const theme = useTheme();
  const performanceMetrics = analysis.performanceMetricsAnalysis;

  // Helper function to get trend icon and color
  const getTrendIndicator = (trendText: string) => {
    const trend = trendText.toLowerCase();
    if (trend.includes('increas') || trend.includes('improv') || trend.includes('up') || trend.includes('rising')) {
      return { icon: TrendingUpIcon, color: theme.palette.success.main, label: 'Positive' };
    } else if (trend.includes('decreas') || trend.includes('declin') || trend.includes('down') || trend.includes('falling')) {
      return { icon: TrendingDownIcon, color: theme.palette.error.main, label: 'Negative' };
    } else {
      return { icon: TrendingFlatIcon, color: theme.palette.warning.main, label: 'Stable' };
    }
  };

  // Helper function to extract performance level from text
  const getPerformanceLevel = (analysisText: string) => {
    const text = analysisText.toLowerCase();
    if (text.includes('excellent') || text.includes('outstanding') || text.includes('strong')) return 85;
    if (text.includes('good') || text.includes('above average')) return 70;
    if (text.includes('average') || text.includes('moderate')) return 50;
    if (text.includes('below') || text.includes('poor') || text.includes('weak')) return 30;
    return 60; // default
  };

  return (
    <StyledCard>
      <StyledCardHeader
        title="Performance Metrics Analysis"
        subheader="ADR, RevPAR, and occupancy insights"
        avatar={
          <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
            <ShowChartIcon />
          </Avatar>
        }
      />
      <StyledCardContent>
        {/* Key Metrics Analysis */}
        {(performanceMetrics?.adrAnalysis || performanceMetrics?.revparAnalysis || performanceMetrics?.occupancyAnalysis) && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* ADR Analysis */}
            {performanceMetrics?.adrAnalysis && (
              <Grid item xs={12} md={4}>
                <Box sx={{
                  p: 2.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  height: '100%'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1, fontSize: '1rem' }}>
                    💰 Average Daily Rate
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={getPerformanceLevel(performanceMetrics.adrAnalysis)}
                    sx={{ mb: 1.5, height: 6, borderRadius: 3 }}
                    color="primary"
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.4 }}>
                    {performanceMetrics.adrAnalysis}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* RevPAR Analysis */}
            {performanceMetrics?.revparAnalysis && (
              <Grid item xs={12} md={4}>
                <Box sx={{
                  p: 2.5,
                  bgcolor: alpha(theme.palette.success.main, 0.03),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                  height: '100%'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.main, mb: 1, fontSize: '1rem' }}>
                    📈 Revenue Per Available Room
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={getPerformanceLevel(performanceMetrics.revparAnalysis)}
                    sx={{ mb: 1.5, height: 6, borderRadius: 3 }}
                    color="success"
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.4 }}>
                    {performanceMetrics.revparAnalysis}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Occupancy Analysis */}
            {performanceMetrics?.occupancyAnalysis && (
              <Grid item xs={12} md={4}>
                <Box sx={{
                  p: 2.5,
                  bgcolor: alpha(theme.palette.info.main, 0.03),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                  height: '100%'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.info.main, mb: 1, fontSize: '1rem' }}>
                    🏨 Occupancy Rate
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={getPerformanceLevel(performanceMetrics.occupancyAnalysis)}
                    sx={{ mb: 1.5, height: 6, borderRadius: 3 }}
                    color="info"
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.4 }}>
                    {performanceMetrics.occupancyAnalysis}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        )}

        {/* Trend Analysis */}
        {(performanceMetrics?.adrTrends || performanceMetrics?.revparTrends || performanceMetrics?.occupancyTrends || performanceMetrics?.trends) && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 2 }}>
              Performance Trends
            </Typography>
            <Grid container spacing={2}>
              {/* Individual Trends */}
              {performanceMetrics?.adrTrends && (
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: alpha(theme.palette.background.paper, 0.5), borderRadius: 1.5 }}>
                    {(() => {
                      const trend = getTrendIndicator(performanceMetrics.adrTrends);
                      const TrendIcon = trend.icon;
                      return (
                        <>
                          <TrendIcon sx={{ color: trend.color, fontSize: 20 }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary">ADR Trend</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                              {trend.label}
                            </Typography>
                          </Box>
                        </>
                      );
                    })()}
                  </Box>
                </Grid>
              )}

              {performanceMetrics?.revparTrends && (
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: alpha(theme.palette.background.paper, 0.5), borderRadius: 1.5 }}>
                    {(() => {
                      const trend = getTrendIndicator(performanceMetrics.revparTrends);
                      const TrendIcon = trend.icon;
                      return (
                        <>
                          <TrendIcon sx={{ color: trend.color, fontSize: 20 }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary">RevPAR Trend</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                              {trend.label}
                            </Typography>
                          </Box>
                        </>
                      );
                    })()}
                  </Box>
                </Grid>
              )}

              {performanceMetrics?.occupancyTrends && (
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: alpha(theme.palette.background.paper, 0.5), borderRadius: 1.5 }}>
                    {(() => {
                      const trend = getTrendIndicator(performanceMetrics.occupancyTrends);
                      const TrendIcon = trend.icon;
                      return (
                        <>
                          <TrendIcon sx={{ color: trend.color, fontSize: 20 }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary">Occupancy Trend</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                              {trend.label}
                            </Typography>
                          </Box>
                        </>
                      );
                    })()}
                  </Box>
                </Grid>
              )}
            </Grid>

            {/* General Trends */}
            {performanceMetrics?.trends && (
              <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.03), borderRadius: 1.5 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                  Overall Trends
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.4 }}>
                  {performanceMetrics.trends}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Recommendations */}
        {performanceMetrics?.recommendations && performanceMetrics.recommendations.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 2 }}>
                Performance Improvement Recommendations
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {performanceMetrics.recommendations.map((recommendation, index) => (
                  <MetricChip
                    key={index}
                    label={formatDisplayItem(recommendation)}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          </>
        )}

        {/* Empty State */}
        {!performanceMetrics && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No performance metrics analysis available. Include performance metrics in your analysis configuration.
            </Typography>
          </Box>
        )}
      </StyledCardContent>
    </StyledCard>
  );
};

export default PerformanceMetricsCard;